import { match } from "ts-pattern";
import {
    type HiddenCols,
    type TableHeader,
    showTableColumns,
} from "../components/ReusableComponents/Table";
import { initTableHeaders } from "../components/DriverList";
import { initDriverDeclarationsTableHeaders } from "../components/DriverDeclarationsTable";
import { initGreyFleetTableHeaders } from "../components/AdminGreyFleetPage";
import { isS2dAdminOrMember } from "../auth/userAccessHelpers";
import useAuth from "../auth";
import { useEffect } from "react";
import useTableStore from "../stores/useTableStore";

/** control the order and hidden properties of table headers remotely for any table */

export const tableHeaderOptionsEnum = {
    allDrivers: 0,
    professionalDrivers: 1,
    declarations: 2,
    greyFleet: 3,
} as const;

export type TableHeaderOptionsType =
    (typeof tableHeaderOptionsEnum)[keyof typeof tableHeaderOptionsEnum];

export const useTableHeaders = (
    option: TableHeaderOptionsType,
    initialise = false
) => {
    const { decodedToken } = useAuth();
    const s2dAdminOrMember = isS2dAdminOrMember(decodedToken);
    const canViewDeletedDrivers = s2dAdminOrMember;

    /** determine whether deletedAt col is ever visible */
    const driverListTableHeaders = () => {
        if (!canViewDeletedDrivers) {
            const filteredTableHeaders = initTableHeaders.filter(
                (header) => header.id !== "deletedAt"
            );

            return filteredTableHeaders;
        } else {
            // If permissions are met set table headers as normal, which includes deletedAt
            return initTableHeaders;
        }
    };

    const getInitialTableHeaders = () => {
        return match(option)
            .with(
                tableHeaderOptionsEnum.allDrivers,
                tableHeaderOptionsEnum.professionalDrivers,
                () => driverListTableHeaders()
            )
            .with(
                tableHeaderOptionsEnum.declarations,
                () => initDriverDeclarationsTableHeaders
            )
            .with(
                tableHeaderOptionsEnum.greyFleet,
                () => initGreyFleetTableHeaders
            )
            .exhaustive();
    };

    const getTableHeaders = useTableStore((state) => state.getTableHeaders);
    const tableHeaders = getTableHeaders(option);
    const getHiddenCols = useTableStore((state) => state.getHiddenCols);
    const hiddenCols = getHiddenCols(option);
    const setTableHeaders = useTableStore((state) => state.setTableHeaders);
    const setHiddenCols = useTableStore((state) => state.setHiddenCols);

    const updateTableConfig = (columns: string[] = []) => {
        const initialHeaders: TableHeader[] = getInitialTableHeaders();
        const initialHiddenCols: HiddenCols[] = initialHeaders.map((item) => ({
            id: item.id,
            hidden: item.hide ?? false,
        }));

        const existingHeaderIds = new Set(tableHeaders.map((header) => header.id));
        const missingHeaders = initialHeaders.filter(
            (header) => !existingHeaderIds.has(header.id)
        );

        if (tableHeaders.length === 0) {
            setTableHeaders(initialHeaders, option);
        } else if (missingHeaders.length > 0) {
            setTableHeaders([...tableHeaders, ...missingHeaders], option);
        }

        const existingHiddenIds = new Set(hiddenCols.map((col) => col.id));
        const missingHiddenCols = initialHiddenCols.filter(
            (col) => !existingHiddenIds.has(col.id)
        );

        if (columns.length > 0) {
            setHiddenCols(showTableColumns(initialHiddenCols, columns), option);
        } else if (hiddenCols.length === 0) {
            setHiddenCols(initialHiddenCols, option);
        } else if (missingHiddenCols.length > 0) {
            setHiddenCols([...hiddenCols, ...missingHiddenCols], option);
        }
    };

    useEffect(() => {
        if (initialise) {
            updateTableConfig();
        }
    }, []);

    return {
        setTableHeaders,
        setHiddenCols,
        updateTableConfig,
    };
};
